/**
 * The arithmetic behind moving things between locations: which rows of the
 * tree a search leaves showing, what a drag is carrying, and how to put a move
 * back. Kept out of the components so it can be tested without a browser.
 */

import { pathLabel, type LocationNode } from "@iltocat/shared";

/** A drag of items, told apart from a photo or a link dropped from elsewhere. */
export const DRAG_TYPE = "application/x-utucat-items";

/** A drag of one location, from the tree, onto another. */
export const LOCATION_DRAG_TYPE = "application/x-utucat-location";

/**
 * A location and everything beneath it: the places it cannot be moved into,
 * since that would cut the branch off the tree.
 */
export function subtreeIds(node: LocationNode): Set<string> {
  const ids = new Set<string>();
  const walk = (at: LocationNode) => {
    if (ids.has(at.id)) return;
    ids.add(at.id);
    at.children.forEach(walk);
  };
  walk(node);
  return ids;
}

export interface TreeMatch {
  /** Rows to draw: every hit and every step above one. */
  visible: Set<string>;
  /** Rows to open, so a hit three levels down is on screen without a click. */
  open: Set<string>;
  /** Hits in the order they are drawn, so Enter takes the one at the top. */
  hits: string[];
}

/**
 * Which locations a typed search finds, or null when nothing is typed.
 *
 * It matches against the whole path, as the capture picker does: "shelf a bin"
 * finds "Basement › Shelf A › Bin 3", which neither word begins.
 */
export function matchLocations(tree: readonly LocationNode[], typed: string): TreeMatch | null {
  const words = typed.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;

  const match: TreeMatch = { visible: new Set(), open: new Set(), hits: [] };
  const walk = (node: LocationNode, above: string[]): boolean => {
    const label = pathLabel(node.path).toLowerCase();
    const hit = words.every((word) => label.includes(word));
    if (hit) match.hits.push(node.id);
    let below = false;
    for (const child of node.children) below = walk(child, [...above, node.id]) || below;
    if (hit || below) match.visible.add(node.id);
    if (below) match.open.add(node.id);
    return hit || below;
  };
  for (const node of tree) walk(node, []);
  return match;
}

/** Every location above this one, root first. An unknown id has none. */
export function ancestorIds(byId: ReadonlyMap<string, LocationNode>, id: string | null): string[] {
  const chain: string[] = [];
  const seen = new Set<string>();
  let at = id ? byId.get(id) : undefined;
  while (at?.parentId && !seen.has(at.parentId)) {
    seen.add(at.parentId);
    chain.unshift(at.parentId);
    at = byId.get(at.parentId);
  }
  return chain;
}

/**
 * What a drag starting on one item carries. Dragging a ticked item takes every
 * ticked one with it, the way a file manager does; dragging an unticked one
 * takes only that one, so a stray drag cannot move a selection nobody meant.
 */
export function dragIds(picked: ReadonlySet<string>, id: string): string[] {
  return picked.has(id) ? [id, ...[...picked].filter((other) => other !== id)] : [id];
}

/** The ids in a drop, or null for anything that is not a drag of items. */
export function readDrag(data: string): string[] | null {
  try {
    const parsed: unknown = JSON.parse(data);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    return parsed.every((id): id is string => typeof id === "string" && id.length > 0) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * How to put a move back: where each item was, grouped so that one location
 * is one pass. Items that were already where they were sent are left out, as
 * nothing happened to them.
 */
export function undoPlan(
  moved: readonly { id: string; from: string | null }[],
  to: string | null,
): { to: string | null; ids: string[] }[] {
  const groups = new Map<string | null, string[]>();
  for (const { id, from } of moved) {
    if (from === to) continue;
    const ids = groups.get(from) ?? [];
    ids.push(id);
    groups.set(from, ids);
  }
  return [...groups].map(([back, ids]) => ({ to: back, ids }));
}
